'use client';

import { useEffect, useMemo, useState } from 'react';
import { Loader2, PhoneForwarded } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';

/** Shape de GET /telefonia/transferencia/alvos. */
interface TransferMember {
  agent_id: number;
  name: string;
  extension: string;
}

interface TransferTeam {
  id: number;
  name: string;
  queue_id: number | null;
  members: TransferMember[];
}

interface TransferTargets {
  teams: TransferTeam[];
  all: TransferMember[];
}

type TransferMode = 'fila' | 'colega';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** unique_id (motor) da chamada ativa do agente — alvo do POST de transfer. */
  callUid: string | null;
  /** Ramal do próprio agente em call — excluído da lista de destinos. */
  ownExtension: string | null;
  /** Disparado após blind transfer OK — encerra estado local da chamada. */
  onTransferred: () => void;
}

export function WebphoneTransferDialog({
  open,
  onOpenChange,
  callUid,
  ownExtension,
  onTransferred,
}: Props) {
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');
  const [mode, setMode] = useState<TransferMode>('colega');
  // agent_id selecionado (string no Select, number no body).
  const [selectedAgentId, setSelectedAgentId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [targets, setTargets] = useState<TransferTargets>({ teams: [], all: [] });

  // Busca os alvos de transferência ao abrir. O payload já vem pronto — a
  // fila do Asterisk entra pelo vínculo team.queue_id, sem chamada separada
  // a /ramais/vinculaveis (painel único ancorado na equipe, sem abas).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api
      .request<TransferTargets>('/telefonia/transferencia/alvos')
      .then((t) => {
        if (!cancelled) setTargets(t);
      })
      .catch((err) => {
        console.warn('[transfer] falha ao buscar alvos:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const isAllTeams = selectedTeamId === 'all';

  const selectedTeam = useMemo(
    () =>
      selectedTeamId && !isAllTeams
        ? (targets.teams.find((t) => String(t.id) === selectedTeamId) ?? null)
        : null,
    [targets, selectedTeamId, isAllTeams],
  );

  const queueEnabled = !!selectedTeam?.queue_id;

  // Membros elegíveis do alvo escolhido (equipe ou "todas"), excluindo o
  // próprio ramal em chamada. O destino é o agente; o ramal é só pra exibir.
  const targetColleagues = useMemo(() => {
    const pool = isAllTeams ? targets.all : (selectedTeam?.members ?? []);
    return pool
      .filter((m) => m.extension && m.extension !== ownExtension)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [targets, isAllTeams, selectedTeam, ownExtension]);

  const colleagueEnabled = targetColleagues.length > 0;

  const selectedColleague = useMemo(
    () => targetColleagues.find((c) => String(c.agent_id) === selectedAgentId),
    [targetColleagues, selectedAgentId],
  );

  // Trocar de equipe reseta o colega e recalcula o modo default: fila quando
  // a equipe tem queue_id; senão colega. "Todas as equipes" é sempre colega.
  const handleTeamChange = (v: string | null) => {
    if (!v) return;
    setSelectedTeamId(v);
    setSelectedAgentId('');
    if (v === 'all') {
      setMode('colega');
      return;
    }
    const team = targets.teams.find((t) => String(t.id) === v);
    setMode(team?.queue_id ? 'fila' : 'colega');
  };

  const close = () => {
    setSelectedTeamId('');
    setMode('colega');
    setSelectedAgentId('');
    onOpenChange(false);
  };

  const canConfirm =
    !!callUid && (mode === 'fila' ? !!selectedTeam?.queue_id : !!selectedColleague);

  const handleTransfer = async () => {
    if (!callUid) {
      toast.error('Não foi possível identificar a chamada para transferir.');
      return;
    }

    if (mode === 'fila') {
      if (!selectedTeam?.queue_id) {
        toast.error('Selecione uma equipe com fila.');
        return;
      }
      setSubmitting(true);
      try {
        await api.request<Record<string, unknown>>(
          `/telefonia/chamadas/${encodeURIComponent(callUid)}/transfer`,
          {
            method: 'POST',
            body: JSON.stringify({ queueId: Number(selectedTeam.queue_id), type: 'blind' }),
          },
        );
        toast.success(`Chamada transferida para a fila de ${selectedTeam.name}.`);
        close();
        onTransferred();
      } catch (err) {
        toast.error(`Falha ao transferir: ${(err as Error).message}`);
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (!selectedColleague) {
      toast.error('Selecione um colega de destino.');
      return;
    }
    setSubmitting(true);
    try {
      // Contrato do motor: { target_agent_id, type:'blind' }. A Platform manda
      // targetAgentId (camelCase); o controller monta o body snake pro motor.
      await api.request<Record<string, unknown>>(
        `/telefonia/chamadas/${encodeURIComponent(callUid)}/transfer`,
        {
          method: 'POST',
          body: JSON.stringify({
            targetAgentId: selectedColleague.agent_id,
            type: 'blind',
          }),
        },
      );
      toast.success(`Chamada transferida para ${selectedColleague.name}.`);
      close();
      onTransferred();
    } catch (err) {
      toast.error(`Falha ao transferir: ${(err as Error).message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PhoneForwarded className="h-5 w-5" />
            Transferir chamada
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="transfer-team">Equipe/Fila de destino</Label>
            <Select value={selectedTeamId} onValueChange={handleTeamChange}>
              <SelectTrigger id="transfer-team">
                <SelectValue placeholder="Selecione uma equipe">
                  {isAllTeams ? 'Todas as equipes' : (selectedTeam?.name ?? 'Selecione uma equipe')}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as equipes</SelectItem>
                {targets.teams.map((t) => (
                  <SelectItem key={t.id} value={String(t.id)}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedTeamId && !isAllTeams && (
            <div role="radiogroup" aria-label="Modo de transferência" className="space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="transfer-mode"
                  value="fila"
                  checked={mode === 'fila'}
                  disabled={!queueEnabled}
                  onChange={() => setMode('fila')}
                  className="h-4 w-4"
                />
                Enviar pra fila (próximo agente livre)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="transfer-mode"
                  value="colega"
                  checked={mode === 'colega'}
                  disabled={!colleagueEnabled}
                  onChange={() => setMode('colega')}
                  className="h-4 w-4"
                />
                Direto pra um colega
              </label>
            </div>
          )}

          {selectedTeamId && mode === 'colega' && (
            <div className="space-y-1.5">
              <Label htmlFor="transfer-colega">Colega de destino</Label>
              <Select value={selectedAgentId} onValueChange={(v) => v && setSelectedAgentId(v)}>
                <SelectTrigger id="transfer-colega">
                  <SelectValue placeholder="Selecione um colega">
                    {selectedColleague
                      ? `${selectedColleague.name} — Ramal ${selectedColleague.extension}`
                      : 'Selecione um colega'}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {targetColleagues.length === 0 ? (
                    <div className="px-2 py-3 text-sm text-muted-foreground">
                      Nenhum colega disponível.
                    </div>
                  ) : (
                    targetColleagues.map((c) => (
                      <SelectItem key={c.agent_id} value={String(c.agent_id)}>
                        {c.name} — Ramal {c.extension}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            Transferência cega — a chamada vai direto para o destino escolhido.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={submitting}>
            Cancelar
          </Button>
          <Button onClick={handleTransfer} disabled={submitting || !canConfirm}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Transferir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
